import React from 'react';
import { BiCheckCircle, BiCircle, BiX } from 'react-icons/bi';

const OnboardingChecklist = ({ progress, horizontal = false, onDismiss = null }) => {
    
    // Safely extract completed events from the API response
    const completedEvents = progress?.completed_events || [];

    // Define the exact 6 core steps requested
    const steps = [
        { id: 'first_case_created', label: 'Create your first case', isCompleted: completedEvents.includes('first_case_created') },
        { id: 'first_client_added', label: 'Add a client/contact', isCompleted: completedEvents.includes('first_client_added') },
        { id: 'first_document_uploaded', label: 'Upload or attach a document', isCompleted: completedEvents.includes('first_document_uploaded') },
        { id: 'first_activity_logged', label: 'Create a service attempt/activity', isCompleted: completedEvents.includes('first_activity_logged') },
        { id: 'first_report_generated', label: 'Generate an affidavit/report', isCompleted: completedEvents.includes('first_report_generated') },
        { id: 'workflow_reviewed', label: 'Review the completed workflow', isCompleted: completedEvents.includes('workflow_reviewed') },
    ];

    // Calculate exact percentage based on the 6 tasks
    const coreStepsIds = steps.map(step => step.id);
    const completedCoreCount = coreStepsIds.filter(id => completedEvents.includes(id)).length;
    const exactPercentage = Math.round((completedCoreCount / coreStepsIds.length) * 100);
    
    const isTrialActivated = completedCoreCount === coreStepsIds.length;

    if (horizontal) {
        return (
            <div className="relative bg-white rounded-xl shadow-md border border-gray-100 p-6 mb-8">
                {/* Close / Cross button - only visible and active when trial activation checklist is complete */}
                {isTrialActivated && onDismiss && (
                    <button
                        onClick={onDismiss}
                        className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Close and remove checklist permanently from dashboard"
                        aria-label="Close and remove checklist permanently from dashboard"
                    >
                        <BiX className="h-5 w-5" />
                    </button>
                )}

                <div className={`flex flex-col md:flex-row justify-between items-start md:items-center mb-6 ${isTrialActivated && onDismiss ? 'pr-8' : ''}`}>
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">Trial Activation Checklist</h3>
                        <p className="text-sm text-gray-500 mt-1">Complete these steps to master the workflow</p>
                    </div>
                    <div className="w-full md:w-64 mt-4 md:mt-0">
                        <div className="flex justify-between text-xs font-medium text-gray-500 mb-1">
                            <span>Overall Progress</span>
                            <span>{exactPercentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                                className="bg-green-500 h-2 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${exactPercentage}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                    {steps.map((step, index) => (
                        <div key={index} className={`flex items-center p-3 rounded-lg border transition-colors ${step.isCompleted ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'}`}>
                            {step.isCompleted ? (
                                <BiCheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mr-2" />
                            ) : (
                                <BiCircle className="h-5 w-5 text-gray-300 flex-shrink-0 mr-2" />
                            )}
                            <span className={`text-xs font-semibold leading-tight ${step.isCompleted ? 'text-gray-900 opacity-60' : 'text-gray-700'}`}>
                                {step.label}
                            </span>
                        </div>
                    ))}
                </div>
                
                {isTrialActivated && (
                    <div className="mt-4 bg-blue-50 p-3 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3 border border-blue-100">
                        <span className="text-blue-700 font-bold text-sm">🎉 Excellent! You've completed the trial activation workflow.</span>
                        {onDismiss && (
                            <button
                                onClick={onDismiss}
                                className="text-xs bg-white text-blue-700 font-semibold px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-100 shadow-sm transition flex-shrink-0"
                            >
                                Remove from Dashboard
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="relative bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="relative bg-gray-50 p-6 border-b border-gray-100">
                {isTrialActivated && onDismiss && (
                    <button
                        onClick={onDismiss}
                        className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors focus:outline-none"
                        title="Close and remove checklist permanently"
                        aria-label="Close and remove checklist permanently"
                    >
                        <BiX className="h-5 w-5" />
                    </button>
                )}
                <h3 className="text-lg font-bold text-gray-800">Trial Activation Checklist</h3>

                <p className="text-sm text-gray-500 mt-1">Complete one full workflow</p>
                
                {/* Progress Bar */}
                <div className="mt-4">
                    <div className="flex justify-between text-xs font-medium text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{exactPercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${exactPercentage}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            <div className="p-6">
                <ul className="space-y-4">
                    {steps.map((step, index) => (
                        <li key={index} className="flex items-start">
                            {step.isCompleted ? (
                                <BiCheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mr-3" />
                            ) : (
                                <BiCircle className="h-6 w-6 text-gray-300 flex-shrink-0 mr-3" />
                            )}
                            <span className={`text-sm font-medium ${step.isCompleted ? 'text-gray-900 line-through opacity-60' : 'text-gray-600'}`}>
                                {step.label}
                            </span>
                        </li>
                    ))}
                    
                    {/* Final Step Message */}
                    <li className="flex items-start pt-2 border-t border-gray-100 mt-2">
                        {isTrialActivated ? (
                            <BiCheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mr-3" />
                        ) : (
                            <BiCircle className="h-6 w-6 text-gray-300 flex-shrink-0 mr-3" />
                        )}
                        <span className={`text-sm font-bold ${isTrialActivated ? 'text-blue-600' : 'text-gray-400'}`}>
                            Workflow Setup Complete
                        </span>
                    </li>
                </ul>
            </div>
            
            {isTrialActivated && (
                <div className="bg-green-50 p-4 text-center border-t border-green-100">
                    <span className="text-green-700 font-bold text-sm">🎉 You are ready to go!</span>
                </div>
            )}
        </div>
    );
};

export default OnboardingChecklist;